import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, RotateCcw, ChevronDown, ChevronRight, Plus, Trash2, GripVertical, ExternalLink, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/platform/cms`;
const SITE = process.env.REACT_APP_BACKEND_URL;

const SectionEditor = ({ title, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-slate-700 rounded-lg overflow-hidden mb-4">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 bg-slate-800/50 text-left hover:bg-slate-800 transition-colors">
        <span className="text-sm font-bold text-white">{title}</span>
        {open ? <ChevronDown size={16} className="text-slate-400" /> : <ChevronRight size={16} className="text-slate-400" />}
      </button>
      {open && <div className="p-5 space-y-4">{children}</div>}
    </div>
  );
};

const Field = ({ label, value, onChange, multiline, placeholder }) => (
  <div>
    <Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">{label}</Label>
    {multiline ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white min-h-[80px] focus:border-violet-500 focus:outline-none" />
    ) : (
      <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="bg-slate-800 border-slate-700 text-white text-sm" />
    )}
  </div>
);

const TagEditor = ({ tags, onChange }) => {
  const [newTag, setNewTag] = useState('');
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {(tags || []).map((t, i) => (
          <span key={`mod-${i}`} className="flex items-center gap-1 px-2 py-1 bg-slate-700 rounded text-xs text-white">
            {t}
            <button onClick={() => onChange(tags.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-300 ml-1"><Trash2 size={10} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="Add tag..." className="bg-slate-800 border-slate-700 text-white text-sm flex-1" onKeyDown={e => { if (e.key === 'Enter' && newTag.trim()) { onChange([...(tags || []), newTag.trim()]); setNewTag(''); } }} />
        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => { if (newTag.trim()) { onChange([...(tags || []), newTag.trim()]); setNewTag(''); } }}><Plus size={14} /></Button>
      </div>
    </div>
  );
};

// ==================== LANDING PAGE EDITOR ====================
function LandingEditor() {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);
  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchContent = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/landing/edit`, { headers: getHeaders() });
      setContent(res.data.content);
      setLastUpdated(res.data.last_updated);
      setUpdatedBy(res.data.updated_by);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchContent(); }, [fetchContent]);

  const update = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!isNaN(parts[i])) obj = obj[parseInt(parts[i])];
        else obj = obj[parts[i]];
      }
      const lastKey = parts[parts.length - 1];
      if (!isNaN(lastKey)) obj[parseInt(lastKey)] = value;
      else obj[lastKey] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/landing`, { content }, { headers: getHeaders() });
      toast.success('Landing page saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset landing page to defaults?')) return;
    try {
      const res = await axios.post(`${API}/landing/reset`, {}, { headers: getHeaders() });
      setContent(res.data.content);
      toast.success('Reset to defaults');
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" /></div>;
  if (!content) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-slate-400">Edit hero, USPs, CTA, and footer. Changes go live after save.</p>
          {lastUpdated && <p className="text-xs text-slate-500 mt-1">Last updated: {new Date(lastUpdated).toLocaleString()} {updatedBy ? `by ${updatedBy}` : ''}</p>}
        </div>
        <div className="flex items-center gap-2">
          <a href={`${SITE}/landing`} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><ExternalLink size={12} /> Preview</a>
          <Button variant="outline" size="sm" onClick={handleReset} className="border-slate-600 text-slate-300"><RotateCcw size={14} className="mr-1" /> Reset</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white"><Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save & Publish'}</Button>
        </div>
      </div>

      <SectionEditor title="Hero Section" defaultOpen={true}>
        <Field label="Badge" value={content.hero?.badge} onChange={v => update('hero.badge', v)} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Title" value={content.hero?.title} onChange={v => update('hero.title', v)} />
          <Field label="Highlight" value={content.hero?.title_highlight} onChange={v => update('hero.title_highlight', v)} />
        </div>
        <Field label="Tagline" value={content.hero?.tagline} onChange={v => update('hero.tagline', v)} />
        <Field label="Description" value={content.hero?.description} onChange={v => update('hero.description', v)} multiline />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Primary CTA" value={content.hero?.cta_primary} onChange={v => update('hero.cta_primary', v)} />
          <Field label="Secondary CTA" value={content.hero?.cta_secondary} onChange={v => update('hero.cta_secondary', v)} />
        </div>
        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Bullets</Label><TagEditor tags={content.hero?.bullets} onChange={v => update('hero.bullets', v)} /></div>
      </SectionEditor>
      <SectionEditor title="Why Recruitizr — Header"><Field label="Badge" value={content.usps_header?.badge} onChange={v => update('usps_header.badge', v)} /><Field label="Title" value={content.usps_header?.title} onChange={v => update('usps_header.title', v)} /><Field label="Description" value={content.usps_header?.description} onChange={v => update('usps_header.description', v)} multiline /></SectionEditor>
      <SectionEditor title="Why Recruitizr — Main Block"><Field label="Title" value={content.usps_main?.title} onChange={v => update('usps_main.title', v)} /><Field label="Description" value={content.usps_main?.description} onChange={v => update('usps_main.description', v)} multiline /><div><Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Tags</Label><TagEditor tags={content.usps_main?.tags} onChange={v => update('usps_main.tags', v)} /></div></SectionEditor>
      <SectionEditor title="Feature Tiles (01-04)">
        {(content.usps_tiles || []).map((tile, i) => (
          <div key={`section-${i}`} className="p-4 bg-slate-800/50 rounded-lg border border-slate-700 space-y-3">
            <span className="text-xs font-bold text-violet-400">Tile {tile.number}</span>
            <Field label="Title" value={tile.title} onChange={v => update(`usps_tiles.${i}.title`, v)} />
            <Field label="Description" value={tile.description} onChange={v => update(`usps_tiles.${i}.description`, v)} multiline />
          </div>
        ))}
      </SectionEditor>
      <SectionEditor title="Bottom CTA"><Field label="Title" value={content.cta_section?.title} onChange={v => update('cta_section.title', v)} /><Field label="Description" value={content.cta_section?.description} onChange={v => update('cta_section.description', v)} multiline /><Field label="Button" value={content.cta_section?.button_text} onChange={v => update('cta_section.button_text', v)} /></SectionEditor>
      <SectionEditor title="Footer">
        <Field label="Email" value={content.footer?.email} onChange={v => update('footer.email', v)} />
        <Field label="Tagline" value={content.footer?.tagline} onChange={v => update('footer.tagline', v)} />
        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Company Links</Label>
          {(content.footer?.company_links || []).map((link, i) => (<div key={`cl-${i}`} className="flex gap-2 mb-2"><Input value={link.label} onChange={e => { const l=[...content.footer.company_links]; l[i]={...l[i],label:e.target.value}; update('footer.company_links',l); }} className="bg-slate-800 border-slate-700 text-white text-sm flex-1" /><Input value={link.url} onChange={e => { const l=[...content.footer.company_links]; l[i]={...l[i],url:e.target.value}; update('footer.company_links',l); }} className="bg-slate-800 border-slate-700 text-white text-sm flex-1" /><Button size="icon" variant="ghost" onClick={() => update('footer.company_links', content.footer.company_links.filter((_,j)=>j!==i))}><Trash2 size={14} className="text-red-400"/></Button></div>))}
          <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => update('footer.company_links',[...(content.footer?.company_links||[]),{label:'',url:''}])}><Plus size={14} className="mr-1"/> Add</Button>
        </div>
        <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Policy Links</Label>
          {(content.footer?.policy_links || []).map((link, i) => (<div key={`pl-${i}`} className="flex gap-2 mb-2"><Input value={link.label} onChange={e => { const l=[...content.footer.policy_links]; l[i]={...l[i],label:e.target.value}; update('footer.policy_links',l); }} className="bg-slate-800 border-slate-700 text-white text-sm flex-1" /><Input value={link.url} onChange={e => { const l=[...content.footer.policy_links]; l[i]={...l[i],url:e.target.value}; update('footer.policy_links',l); }} className="bg-slate-800 border-slate-700 text-white text-sm flex-1" /><Button size="icon" variant="ghost" onClick={() => update('footer.policy_links', content.footer.policy_links.filter((_,j)=>j!==i))}><Trash2 size={14} className="text-red-400"/></Button></div>))}
          <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => update('footer.policy_links',[...(content.footer?.policy_links||[]),{label:'',url:''}])}><Plus size={14} className="mr-1"/> Add</Button>
        </div>
      </SectionEditor>
    </div>
  );
}

// ==================== FEATURE PAGE EDITOR ====================
function FeatureEditor({ slug, onBack }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/features/${slug}/edit`, { headers: getHeaders() })
      .then(r => setContent(r.data.content))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [slug]);

  const update = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!isNaN(parts[i])) obj = obj[parseInt(parts[i])];
        else obj = obj[parts[i]];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/features/${slug}`, { content }, { headers: getHeaders() });
      toast.success(`${content?.badge || slug} page saved`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm(`Reset ${slug} page to defaults?`)) return;
    try {
      const res = await axios.post(`${API}/features/${slug}/reset`, {}, { headers: getHeaders() });
      setContent(res.data.content);
      toast.success('Reset to defaults');
    } catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" /></div>;
  if (!content) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} className="text-slate-400"><ArrowLeft size={18} /></Button>
          <div>
            <p className="text-lg font-bold text-white">{content.badge || slug}</p>
            <p className="text-xs text-slate-400">Edit feature page content</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${SITE}/features/${slug}`} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-white flex items-center gap-1"><ExternalLink size={12} /> Preview</a>
          <Button variant="outline" size="sm" onClick={handleReset} className="border-slate-600 text-slate-300"><RotateCcw size={14} className="mr-1" /> Reset</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white"><Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save & Publish'}</Button>
        </div>
      </div>

      <SectionEditor title="Page Header" defaultOpen={true}>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Badge" value={content.badge} onChange={v => update('badge', v)} />
          <Field label="Badge Color" value={content.badgeColor} onChange={v => update('badgeColor', v)} placeholder="violet, cyan, emerald, amber, indigo" />
        </div>
        <Field label="Title" value={content.title} onChange={v => update('title', v)} />
        <Field label="Subtitle" value={content.subtitle} onChange={v => update('subtitle', v)} />
        <Field label="Description" value={content.description} onChange={v => update('description', v)} multiline />
      </SectionEditor>

      {(content.sections || []).map((section, si) => (
        <SectionEditor key={section._key || `sec-${si}-${section.type || ''}`} title={`Section ${si + 1}: ${section.title || section.type}`}>
          <Field label="Title" value={section.title} onChange={v => { const s = [...content.sections]; s[si] = {...s[si], title: v}; update('sections', s); }} />
          <Field label="Description" value={section.desc} onChange={v => { const s = [...content.sections]; s[si] = {...s[si], desc: v}; update('sections', s); }} multiline />

          {section.type === 'cards' && (
            <div className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Feature Cards</Label>
              {(section.cards || []).map((card, ci) => (
                <div key={card._key || `card-${si}-${ci}`} className="p-3 bg-slate-800/50 rounded border border-slate-700 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-500">{card.icon} &middot; {card.color}</span>
                    <Button size="icon" variant="ghost" onClick={() => { const s = [...content.sections]; s[si] = {...s[si], cards: s[si].cards.filter((_,j)=>j!==ci)}; update('sections', s); }}><Trash2 size={12} className="text-red-400" /></Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input value={card.title} onChange={e => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].cards[ci].title = e.target.value; update('sections', s); }} className="bg-slate-900 border-slate-700 text-white text-xs" placeholder="Title" />
                    <Input value={card.icon} onChange={e => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].cards[ci].icon = e.target.value; update('sections', s); }} className="bg-slate-900 border-slate-700 text-white text-xs" placeholder="Icon name" />
                  </div>
                  <textarea value={card.desc} onChange={e => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].cards[ci].desc = e.target.value; update('sections', s); }} className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-white min-h-[50px]" placeholder="Description" />
                </div>
              ))}
              <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].cards = [...(s[si].cards||[]), {_key: `card-${Date.now()}-${Math.random().toString(36).slice(2,8)}`, icon:'Sparkles',title:'New Card',desc:'Description',color:'violet'}]; update('sections', s); }}><Plus size={14} className="mr-1" /> Add Card</Button>
            </div>
          )}

          {section.type === 'steps' && (
            <div className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Steps</Label>
              {(section.steps || []).map((step, sti) => (
                <div key={step._key || `step-${si}-${sti}`} className="flex gap-3 items-start p-3 bg-slate-800/50 rounded border border-slate-700">
                  <span className="text-sm font-bold text-violet-400 mt-1">{step.number}</span>
                  <div className="flex-1 space-y-2">
                    <Input value={step.title} onChange={e => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].steps[sti].title = e.target.value; update('sections', s); }} className="bg-slate-900 border-slate-700 text-white text-xs" placeholder="Step title" />
                    <Input value={step.desc} onChange={e => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].steps[sti].desc = e.target.value; update('sections', s); }} className="bg-slate-900 border-slate-700 text-white text-xs" placeholder="Step description" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {section.type === 'metrics' && (
            <div className="space-y-3">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-400">Metrics</Label>
              {(section.metrics || []).map((m, mi) => (
                <div key={m._key || `metric-${si}-${mi}`} className="flex gap-2 items-center">
                  <Input value={m.val} onChange={e => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].metrics[mi].val = e.target.value; update('sections', s); }} className="bg-slate-900 border-slate-700 text-white text-xs w-24" placeholder="Value" />
                  <Input value={m.label} onChange={e => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].metrics[mi].label = e.target.value; update('sections', s); }} className="bg-slate-900 border-slate-700 text-white text-xs flex-1" placeholder="Label" />
                  <Button size="icon" variant="ghost" onClick={() => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].metrics = s[si].metrics.filter((_,j)=>j!==mi); update('sections', s); }}><Trash2 size={12} className="text-red-400" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" className="border-slate-600 text-slate-300" onClick={() => { const s = JSON.parse(JSON.stringify(content.sections)); s[si].metrics = [...(s[si].metrics||[]), {_key: `metric-${Date.now()}-${Math.random().toString(36).slice(2,8)}`, val:'',label:''}]; update('sections', s); }}><Plus size={14} className="mr-1" /> Add Metric</Button>
            </div>
          )}
        </SectionEditor>
      ))}
    </div>
  );
}

// ==================== FEATURE PAGES LIST ====================
function FeaturePagesList({ onEdit }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  useEffect(() => {
    axios.get(`${API}/features`, { headers: getHeaders() })
      .then(r => setPages(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600" /></div>;

  return (
    <div className="space-y-3">
      {pages.map(p => (
        <div key={p.slug} className="flex items-center justify-between p-4 bg-slate-800/30 border border-slate-700 rounded-lg hover:bg-slate-800/60 transition-colors">
          <div>
            <p className="font-bold text-white">{p.badge || p.title}</p>
            <p className="text-xs text-slate-500">/features/{p.slug} {p.customized && <span className="text-violet-400 ml-2">customized</span>} {p.last_updated && <span className="ml-2">updated {new Date(p.last_updated).toLocaleDateString()}</span>}</p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`${SITE}/features/${p.slug}`} target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-white"><ExternalLink size={14} /></a>
            <Button size="sm" onClick={() => onEdit(p.slug)} className="bg-violet-600 hover:bg-violet-700 text-white text-xs">Edit Content</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== LEGAL PAGES EDITOR ====================
function LegalPagesList({ onEdit }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  useEffect(() => {
    axios.get(`${API}/legal`, { headers: getHeaders() })
      .then(r => setPages(r.data))
      .catch(() => toast.error('Failed to load legal pages'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  const pageIcons = { privacy: 'Privacy Policy', terms: 'Terms & Conditions', about: 'About Us' };

  return (
    <div className="space-y-3" data-testid="legal-pages-list">
      {pages.map(p => (
        <div key={p.slug} className="flex items-center justify-between bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-colors">
          <div>
            <p className="font-bold text-white">{pageIcons[p.slug] || p.title}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              /{p.slug} {p.customized && <span className="text-violet-400 ml-2">(customized)</span>}
              {p.last_updated && <span className="ml-2">· Updated: {p.last_updated}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a href={`/${p.slug === 'privacy' ? 'privacy' : p.slug === 'terms' ? 'terms' : 'about'}`} target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white"><ExternalLink size={14} /></Button>
            </a>
            <Button variant="ghost" size="sm" className="text-violet-400 hover:text-violet-300" onClick={() => onEdit(p.slug)}>
              Edit
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function LegalEditor({ slug, onBack }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });
  const pageNames = { privacy: 'Privacy Policy', terms: 'Terms & Conditions', about: 'About Us' };

  useEffect(() => {
    axios.get(`${API}/legal/${slug}`, { headers: getHeaders() })
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load page'))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/legal/${slug}`, { content: data }, { headers: getHeaders() });
      toast.success('Page saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset to default content? Your edits will be lost.')) return;
    try {
      const r = await axios.post(`${API}/legal/${slug}/reset`, {}, { headers: getHeaders() });
      setData(r.data.content);
      toast.success('Reset to defaults');
    } catch { toast.error('Reset failed'); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="legal-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" onClick={onBack}>
            <ArrowLeft size={14} className="mr-1" /> Back
          </Button>
          <h3 className="text-lg font-bold text-white">{pageNames[slug] || slug}</h3>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="text-amber-400 hover:text-amber-300" onClick={handleReset}>
            <RotateCcw size={14} className="mr-1" /> Reset to Default
          </Button>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSave} disabled={saving}>
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <SectionEditor title="Page Title & Date" defaultOpen={true}>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-slate-400">Page Title</Label>
            <Input value={data?.title || ''} onChange={e => setData(d => ({ ...d, title: e.target.value }))}
              className="bg-slate-800 border-slate-600 text-white mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Last Updated</Label>
            <Input value={data?.last_updated || ''} onChange={e => setData(d => ({ ...d, last_updated: e.target.value }))}
              className="bg-slate-800 border-slate-600 text-white mt-1" />
          </div>
        </div>
      </SectionEditor>

      <SectionEditor title="Page Content (Markdown)" defaultOpen={true}>
        <p className="text-xs text-slate-500 mb-2">Use ## for headings, **bold** for emphasis, - for bullet points, | for tables. Preview on the live page.</p>
        <textarea
          value={data?.content || ''}
          onChange={e => setData(d => ({ ...d, content: e.target.value }))}
          className="w-full h-[500px] bg-slate-800 border border-slate-600 text-white font-mono text-xs p-4 rounded-lg resize-y"
          placeholder="## Section Title&#10;&#10;Your content here..."
        />
      </SectionEditor>

      <div className="flex items-center gap-2 text-xs text-slate-500">
        <ExternalLink size={12} />
        <a href={`/${slug === 'privacy' ? 'privacy' : slug === 'terms' ? 'terms' : 'about'}`} target="_blank" rel="noreferrer" className="hover:text-violet-400">
          Preview live page →
        </a>
      </div>
    </div>
  );
}

// ==================== PRICING EDITOR ====================
function PricingEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeRegion, setActiveRegion] = useState('inr');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  useEffect(() => {
    axios.get(`${API}/pricing/edit`, { headers: getHeaders() })
      .then(r => setData(r.data.content))
      .catch(() => toast.error('Failed to load pricing'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/pricing`, { content: data }, { headers: getHeaders() });
      toast.success('Pricing saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset pricing to defaults?')) return;
    try {
      const r = await axios.post(`${API}/pricing/reset`, {}, { headers: getHeaders() });
      setData(r.data.content);
      toast.success('Reset to defaults');
    } catch { toast.error('Reset failed'); }
  };

  const handleSyncFromPlans = async () => {
    if (!window.confirm('Sync this CMS Pricing tab from the live plans in Plan Management? Any unsaved edits here will be replaced.')) return;
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/plans/admin/sync-cms`, {}, { headers: getHeaders() });
      // Reload from server
      const r = await axios.get(`${API}/pricing/edit`, { headers: getHeaders() });
      if (r.data?.content) setData(r.data.content);
      toast.success('CMS Pricing synced from live plans');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Sync failed'); }
  };

  const updatePlan = (region, idx, field, value) => {
    setData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[region].plans[idx][field] = value;
      return updated;
    });
  };

  const updateFeature = (region, planIdx, featIdx, value) => {
    setData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[region].plans[planIdx].features[featIdx] = value;
      return updated;
    });
  };

  const addFeature = (region, planIdx) => {
    setData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[region].plans[planIdx].features.push('New feature');
      return updated;
    });
  };

  const removeFeature = (region, planIdx, featIdx) => {
    setData(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      updated[region].plans[planIdx].features.splice(featIdx, 1);
      return updated;
    });
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading...</div>;
  if (!data) return <div className="text-center py-8 text-red-400">Failed to load pricing data</div>;

  const regionData = data[activeRegion];

  return (
    <div className="space-y-4" data-testid="pricing-editor">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-bold text-white">Pricing Plans</h3>
          <a href="/pricing" target="_blank" rel="noreferrer" className="text-xs text-violet-400 hover:text-violet-300 flex items-center gap-1">
            <ExternalLink size={12} /> Preview
          </a>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300" onClick={handleSyncFromPlans} data-testid="cms-pricing-sync-btn">
            <RotateCcw size={14} className="mr-1" /> Sync from Plans
          </Button>
          <Button variant="ghost" size="sm" className="text-amber-400 hover:text-amber-300" onClick={handleReset}>
            <RotateCcw size={14} className="mr-1" /> Reset
          </Button>
          <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white" onClick={handleSave} disabled={saving}>
            <Save size={14} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-emerald-400 -mt-1">This tab automatically mirrors plans from <strong>Plan Management</strong>. Edit prices/features there. Use this tab for the Add-on banner copy.</p>

      {/* Region tabs */}
      <div className="flex gap-1 bg-slate-800/50 p-1 rounded-lg w-fit">
        <button onClick={() => setActiveRegion('inr')}
          className={`px-3 py-1.5 rounded text-xs font-medium ${activeRegion === 'inr' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          India (INR)
        </button>
        <button onClick={() => setActiveRegion('usd')}
          className={`px-3 py-1.5 rounded text-xs font-medium ${activeRegion === 'usd' ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          US (USD)
        </button>
      </div>

      <SectionEditor title="Platform Fee" defaultOpen={false}>
        <Input value={regionData.platform_fee || ''}
          onChange={e => setData(prev => ({ ...prev, [activeRegion]: { ...prev[activeRegion], platform_fee: e.target.value } }))}
          className="bg-slate-800 border-slate-600 text-white"
          placeholder="(no platform fee — leave blank)" />
        <p className="text-[11px] text-slate-500 mt-2">Currently the public website does not display a platform fee. Leave this blank.</p>
      </SectionEditor>

      {regionData.plans.map((plan, idx) => (
        <SectionEditor key={plan.name} title={`${plan.name} Plan ${plan.highlighted ? '(Highlighted)' : ''}`} defaultOpen={idx === 1}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Name</Label>
              <Input value={plan.name} onChange={e => updatePlan(activeRegion, idx, 'name', e.target.value)}
                className="bg-slate-800 border-slate-600 text-white mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Price</Label>
              <Input type="number" value={plan.price} onChange={e => updatePlan(activeRegion, idx, 'price', parseInt(e.target.value) || 0)}
                className="bg-slate-800 border-slate-600 text-white mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Min Users</Label>
              <Input value={plan.min_users} onChange={e => updatePlan(activeRegion, idx, 'min_users', e.target.value)}
                className="bg-slate-800 border-slate-600 text-white mt-1" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Tagline</Label>
              <Input value={plan.tagline} onChange={e => updatePlan(activeRegion, idx, 'tagline', e.target.value)}
                className="bg-slate-800 border-slate-600 text-white mt-1" />
            </div>
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-slate-400">Features</Label>
              <Button variant="ghost" size="sm" className="text-violet-400 h-6 text-xs" onClick={() => addFeature(activeRegion, idx)}>
                <Plus size={12} className="mr-1" /> Add
              </Button>
            </div>
            {plan.features.map((f, fi) => (
              <div key={`${activeRegion}-plan-${idx}-feat-${fi}-${f.slice(0,20)}`} className="flex items-center gap-2 mb-1.5">
                <Input value={f} onChange={e => updateFeature(activeRegion, idx, fi, e.target.value)}
                  className="bg-slate-800 border-slate-600 text-white text-xs h-8" />
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300 flex-shrink-0"
                  onClick={() => removeFeature(activeRegion, idx, fi)}>
                  <Trash2 size={12} />
                </Button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input type="checkbox" checked={plan.highlighted}
              onChange={e => updatePlan(activeRegion, idx, 'highlighted', e.target.checked)} />
            <Label className="text-xs text-slate-400">Highlight as "Most Popular"</Label>
          </div>
        </SectionEditor>
      ))}

      <SectionEditor title="Add-on (Revenue Intelligence Engine)" defaultOpen={true}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-slate-400">Add-on Name</Label>
            <Input value={regionData.addon?.name || ''}
              onChange={e => setData(prev => ({ ...prev, [activeRegion]: { ...prev[activeRegion], addon: { ...prev[activeRegion].addon, name: e.target.value } } }))}
              className="bg-slate-800 border-slate-600 text-white mt-1" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Price Text</Label>
            <Input value={regionData.addon?.price || ''}
              onChange={e => setData(prev => ({ ...prev, [activeRegion]: { ...prev[activeRegion], addon: { ...prev[activeRegion].addon, price: e.target.value } } }))}
              className="bg-slate-800 border-slate-600 text-white mt-1"
              placeholder={activeRegion === 'inr' ? '+₹1,500 / recruiter' : '+$29 / recruiter'} />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Description</Label>
            <Input value={regionData.addon?.description || ''}
              onChange={e => setData(prev => ({ ...prev, [activeRegion]: { ...prev[activeRegion], addon: { ...prev[activeRegion].addon, description: e.target.value } } }))}
              className="bg-slate-800 border-slate-600 text-white mt-1"
              placeholder="Advanced analytics & revenue visibility add-on" />
          </div>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">Shown as a banner under the plan cards on the website. Leave description blank to use the default.</p>
      </SectionEditor>
    </div>
  );
}

// ==================== MAIN CMS PAGE ====================
export default function SuperAdminCMS() {
  const [activeTab, setActiveTab] = useState('landing');
  const [editingFeature, setEditingFeature] = useState(null);
  const [editingLegal, setEditingLegal] = useState(null);

  return (
    <div data-testid="cms-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>Website Content Manager</h1>
        <p className="text-sm text-slate-400">Edit landing page, feature pages, pricing, and legal documents. Changes go live immediately after save.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-800/50 p-1 rounded-lg w-fit flex-wrap">
        <button onClick={() => { setActiveTab('landing'); setEditingFeature(null); setEditingLegal(null); }}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${activeTab === 'landing' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          Landing Page
        </button>
        <button onClick={() => { setActiveTab('features'); setEditingFeature(null); setEditingLegal(null); }}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${activeTab === 'features' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          Feature Pages
        </button>
        <button onClick={() => { setActiveTab('pricing'); setEditingFeature(null); setEditingLegal(null); }}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${activeTab === 'pricing' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          Pricing
        </button>
        <button onClick={() => { setActiveTab('legal'); setEditingFeature(null); setEditingLegal(null); }}
          className={`px-4 py-2 rounded text-sm font-medium transition-colors ${activeTab === 'legal' ? 'bg-violet-600 text-white' : 'text-slate-400 hover:text-white'}`}>
          Legal & Info Pages
        </button>
      </div>

      {activeTab === 'landing' && <LandingEditor />}
      {activeTab === 'features' && !editingFeature && <FeaturePagesList onEdit={setEditingFeature} />}
      {activeTab === 'features' && editingFeature && <FeatureEditor slug={editingFeature} onBack={() => setEditingFeature(null)} />}
      {activeTab === 'pricing' && <PricingEditor />}
      {activeTab === 'legal' && !editingLegal && <LegalPagesList onEdit={setEditingLegal} />}
      {activeTab === 'legal' && editingLegal && <LegalEditor slug={editingLegal} onBack={() => setEditingLegal(null)} />}
    </div>
  );
}
